import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, ClockIcon, CreditCardIcon, DownloadIcon, FileTextIcon, HistoryIcon, ImageIcon, MapPinIcon, PaperclipIcon, PlusIcon, SparklesIcon } from 'lucide-react';
import type { RequestAttachment } from '../../data/demandes';
import { feeStatusLabels, type PermitApplication } from '../../data/permis';
const attachmentIcons: Record<RequestAttachment['type'], typeof ImageIcon> = {
  image: ImageIcon,
  pdf: FileTextIcon,
  form: FileTextIcon
};
export function PermitDetailsPanel({
  permit
}: {
  permit: PermitApplication;
}) {
  const [historyOpen, setHistoryOpen] = useState(false);
  return <div className="space-y-5" data-id="element-2537" data-mp="2f4ee05ny342j">
      <section aria-labelledby="permis-description-titre" className="rounded-3xl bg-ville-sand p-6 sm:p-7" data-id="element-2538" data-mp="2f5fs6j09c6oh">
        <h2 id="permis-description-titre" className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-2539" data-mp="2a406coq99w51">
          Description des travaux
        </h2>
        <p className="mt-3 text-[16px] leading-relaxed text-slate-700" data-id="element-2540" data-mp="1kbjt5mdlh3bo">{permit.description}</p>
        <p className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-slate-500" data-id="element-2541" data-mp="1p3bkhz4krd2v">
          <SparklesIcon className="h-3.5 w-3.5 text-ville-indigo" aria-hidden="true" data-id="element-2542" data-mp="1bbn4vlv4a6h5" />
          Demande créée par {permit.createdBy}
        </p>
      </section>

      <div className="grid gap-5 sm:grid-cols-2" data-id="element-2543" data-mp="1g8dmweqhfc57">
        <section aria-labelledby="permis-lieu-titre" className="rounded-3xl border border-slate-200 p-6" data-id="element-2544" data-mp="23zssqhhgwbl7">
          <h2 id="permis-lieu-titre" className="text-sm font-extrabold uppercase tracking-wider text-slate-500" data-id="element-2545" data-mp="280mhfd2qhuf5">
            Lieu des travaux
          </h2>
          <p className="mt-3 flex items-start gap-2 text-[15px] font-bold leading-snug text-ville-navy" data-id="element-2546" data-mp="1u492obpl474d">
            <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-2547" data-mp="imprnx5rizv9" />
            {permit.address}
          </p>
          <p className="mt-1 pl-6 text-sm text-slate-500" data-id="element-2548" data-mp="nmu6lir0vmjd">{permit.fullAddress}</p>
        </section>

        <section aria-labelledby="permis-frais-titre" className="rounded-3xl border border-slate-200 p-6" data-id="element-2549" data-mp="3imyyvgda4j6">
          <h2 id="permis-frais-titre" className="text-sm font-extrabold uppercase tracking-wider text-slate-500" data-id="element-2550" data-mp="3vos4an7wgddf">
            Frais et délais
          </h2>
          <dl className="mt-3 space-y-2 text-sm" data-id="element-2551" data-mp="3lxxi1iunldrz">
            <div className="flex items-center gap-2" data-id="element-2552" data-mp="3lx5wz1yomyfy">
              <CreditCardIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-2553" data-mp="3gz43ro7jliqr" />
              <dt className="text-slate-500" data-id="element-2554" data-mp="3bzrhb4kl2dop">Frais</dt>
              <dd className="font-bold text-ville-navy" data-id="element-2555" data-mp="3by6mdducwsqb">
                {permit.fee}
                {permit.fee !== 'Gratuit' && <span className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-bold ${permit.feeStatus === 'paye' ? 'bg-ville-mist text-ville-deep' : permit.feeStatus === 'a-payer' ? 'bg-ville-lime text-ville-navy' : 'bg-slate-100 text-slate-600'}`} data-id="element-2556" data-mp="32ytf8rtpgg8r">
                    {feeStatusLabels[permit.feeStatus]}
                  </span>}
              </dd>
            </div>
            <div className="flex items-center gap-2" data-id="element-2557" data-mp="1iivlfxgas2u5">
              <ClockIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-2558" data-mp="1ng57r48e3ydr" />
              <dt className="text-slate-500" data-id="element-2559" data-mp="psn2i3dxteei">Analyse</dt>
              <dd className="font-bold text-ville-navy" data-id="element-2560" data-mp="pu7wnrwcavbu">{permit.processingTime}</dd>
            </div>
          </dl>
          <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-200 pt-4 text-sm" data-id="element-2561" data-mp="zqau4kg8y5sg">
            <div data-id="element-2562" data-mp="zsnjhioc3ifj">
              <dt className="text-xs text-slate-500" data-id="element-2563" data-mp="13rmkwtauwx28">Déposée le</dt>
              <dd className="mt-0.5 font-bold text-ville-navy" data-id="element-2564" data-mp="13ps1xkj5bf0s">{permit.createdOn}</dd>
            </div>
            <div data-id="element-2565" data-mp="2b7sqgyvi034y">
              <dt className="text-xs text-slate-500" data-id="element-2566" data-mp="2f6rsobpuhlsp">Décision</dt>
              <dd className="mt-0.5 font-bold text-ville-navy" data-id="element-2567" data-mp="2f58ecqnr1ktk">{permit.decisionOn ?? '-- / -- / --'}</dd>
            </div>
          </dl>
        </section>
      </div>

      <section aria-labelledby="permis-documents-titre" className="rounded-3xl border border-slate-200 p-6" data-id="element-2568" data-mp="28rumi4x9s76w">
        <h2 id="permis-documents-titre" className="text-sm font-extrabold uppercase tracking-wider text-slate-500" data-id="element-2569" data-mp="2notq7hp03fw3">
          Documents exigés
        </h2>
        <ul className="mt-4 space-y-2" data-id="element-2570" data-mp="2wogtk91aivrs">
          {permit.documents.map(document => <li key={document.id} className="flex items-start gap-3 rounded-2xl bg-slate-50 px-4 py-3" data-id="element-2571" data-mp="2rpndq9yj6sog">
              <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full ${document.received ? 'bg-ville-mist text-ville-deep' : 'bg-slate-200 text-slate-500'}`} aria-hidden="true" data-id="element-2572" data-mp="36ea5jb5chcwk">
                <CheckIcon className="h-3 w-3" data-id="element-2573" data-mp="3fcvi8po4xxpf" />
              </span>
              <div className="min-w-0 flex-1" data-id="element-2574" data-mp="3bf7nbmmg04xj">
                <p className="text-sm font-bold text-ville-navy" data-id="element-2575" data-mp="3u26quvkxeyg5">{document.name}</p>
                <p className="mt-0.5 text-[13px] text-slate-600" data-id="element-2576" data-mp="2vng1gf3b8hu">{document.detail}</p>
              </div>
              <span className={`shrink-0 text-xs font-bold ${document.received ? 'text-ville-deep' : 'text-ville-coral'}`} data-id="element-2577" data-mp="3l18u8yctht7l">
                {document.received ? 'Reçu' : 'À fournir'}
              </span>
            </li>)}
        </ul>
      </section>

      <section aria-labelledby="permis-fichiers-titre" className="rounded-3xl border border-slate-200 p-6" data-id="element-2578" data-mp="3g2feeza25q49">
        <h2 id="permis-fichiers-titre" className="text-sm font-extrabold uppercase tracking-wider text-slate-500" data-id="element-2579" data-mp="32bsfb60dxy7n">
          Fichiers joints
        </h2>
        {permit.attachments.length > 0 ? <ul className="mt-4 space-y-2" data-id="element-2580" data-mp="2sdmhp0lm3e8l">
            {permit.attachments.map(file => {
          const Icon = attachmentIcons[file.type];
          return <li key={file.name} className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3" data-id="element-2581" data-mp="12v7i1hz7ktgo">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white text-ville-deep" aria-hidden="true" data-id="element-2582" data-mp="y47ie4ypfxah">
                    <Icon className="h-4 w-4" data-id="element-2583" data-mp="p4ksogfc8fx9" />
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-2584" data-mp="p2825evikx8l">
                    <span className="block truncate text-sm font-bold text-ville-navy" data-id="element-2585" data-mp="1lta71wrraqeh">{file.name}</span>
                    <span className="block text-xs text-slate-500" data-id="element-2586" data-mp="1hu1fmyk6v4m3">{file.size}</span>
                  </span>
                  <button type="button" aria-label={`Télécharger ${file.name}`} className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-white hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2587" data-mp="1cs2dtt43kfr9">
                    <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-2588" data-mp="3ii5uenx4nnwt" />
                  </button>
                </li>;
        })}
          </ul> : <p className="mt-3 flex items-center gap-2 text-sm text-slate-500" data-id="element-2589" data-mp="3sequqvy7caub">
            <PaperclipIcon className="h-4 w-4" aria-hidden="true" data-id="element-2590" data-mp="1032zi3m8h1u" />
            Aucun fichier joint.
          </p>}
        <button type="button" className="mt-4 inline-flex items-center gap-2 rounded-full border border-dashed border-slate-300 px-4 py-2 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2591" data-mp="50wok4tl5jrk">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-2592" data-mp="2grh7zke0xscb" />
          Ajouter un fichier
        </button>
      </section>

      <section aria-labelledby="permis-historique-titre" className="rounded-3xl border border-slate-200" data-id="element-2593" data-mp="2lpsf3ncxpoqu">
        <button type="button" aria-expanded={historyOpen} onClick={() => setHistoryOpen(open => !open)} className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2594" data-mp="rhokquv7lllf">
          <span className="flex items-center gap-3" data-id="element-2595" data-mp="wahszspvzc29">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-2596" data-mp="8juh6wcuo2af">
              <HistoryIcon className="h-4 w-4" data-id="element-2597" data-mp="3mlwao0bhk5h" />
            </span>
            <span id="permis-historique-titre" className="text-sm font-extrabold uppercase tracking-wider text-slate-500" data-id="element-2598" data-mp="hllk5tg09v0l">
              Historique
            </span>
          </span>
          <motion.span animate={{
          rotate: historyOpen ? 180 : 0
        }} transition={{
          duration: 0.18,
          ease: [0.23, 1, 0.32, 1]
        }} aria-hidden="true" data-id="element-2599" data-mp="1sy4it7sgfw6q">
            <ChevronDownIcon className="h-5 w-5 text-slate-400" data-id="element-2600" data-mp="1wgtrjnh6i94w" />
          </motion.span>
        </button>

        {historyOpen && <ol className="space-y-4 border-t border-slate-100 px-6 py-5" data-id="element-2601" data-mp="21ed9oygzz624">
            {permit.timeline.map(step => <li key={`${step.date}-${step.label}`} className="flex gap-3" data-id="element-2602" data-mp="1ria57lyoavc0">
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-ville-indigo" aria-hidden="true" data-id="element-2603" data-mp="1rkd6j25a0aw1" />
                <div data-id="element-2604" data-mp="2k77o9rapkw5t">
                  <p className="text-sm font-bold text-ville-navy" data-id="element-2605" data-mp="2k7qzkp5usmav">{step.label}</p>
                  <p className="text-xs text-slate-500" data-id="element-2606" data-mp="25ikyswrdao0v">{step.date}</p>
                </div>
              </li>)}
          </ol>}
      </section>
    </div>;
}